/**
 * AdminOnlyDeleteDialog.tsx — `D-41`, the refusal a non-administrator sees.
 *
 * Omar, 2026-09-06: *"Non-admin users attempting deletion should receive only
 * this informational popup"*, and he gave the sentence verbatim. It appears
 * below exactly as he wrote it.
 *
 * **Informational, not a confirmation.** One button, and it dismisses. There is
 * deliberately no second button and no destructive action anywhere in this
 * component: a dialog that refuses an action must not also offer a way to
 * attempt it.
 *
 * **It must be shown BEFORE anything is fetched.** The rule is not only that a
 * member cannot delete — it is that they *"must not receive the affected-summary
 * list through that action."* On the Knowledge Base list the preview query fires
 * the moment `deleteTarget` is set, so every caller renders this INSTEAD of
 * opening the confirmation, never beside it. The server refuses both routes as
 * well; this is the half that keeps the request from being made at all.
 *
 * One component rather than four copies of the sentence: it is shown from the
 * Knowledge Base list, from the Esplora page rail, from «Documenti del progetto»
 * and from the intake review panel, and four literals that must agree are the
 * shape this project has already been bitten by.
 */
import { useEffect, useRef } from "react";

import { I } from "../icons";

/**
 * Omar's sentence, 2026-09-06, verbatim. Exported so tests assert against the
 * same string the screen renders rather than a retyped copy of it — and so the
 * one place it lives is the one place it can be changed.
 *
 * It is deliberately identical to the server's 403 detail
 * (`wiki_llm_service.auth.DELETE_ADMIN_ONLY_IT`). The two are separate strings
 * in separate languages and cannot import each other; `capabilityMirror`-style
 * cross-language pinning is the standing pattern for that, and this pair is
 * covered by `adminOnlyCopyMirror.test.ts`.
 */
export const ADMIN_ONLY_DELETE_MESSAGE =
  "Solo gli amministratori possono eliminare definitivamente contenuti dalla Knowledge Base. " +
  "Per procedere, rivolgiti all’Admin del tuo workspace.";

interface AdminOnlyDeleteDialogProps {
  /** What the person tried to delete. Shown in the title so the refusal is not
   *  ambiguous when several rows are on screen. */
  subject: string;
  onDismiss: () => void;
}

export function AdminOnlyDeleteDialog({
  subject,
  onDismiss,
}: AdminOnlyDeleteDialogProps): JSX.Element {
  const dismissRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    dismissRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onDismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onDismiss]);

  return (
    <div
      onClick={onDismiss}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(20,22,28,0.45)",
        zIndex: 70,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="fade-in"
        role="dialog"
        aria-modal="true"
        aria-labelledby="admin-only-del-title"
        style={{
          width: 520,
          maxWidth: "100%",
          background: "var(--c-bg-elev)",
          borderRadius: 12,
          boxShadow: "var(--sh-pop)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "14px 18px",
            borderBottom: "1px solid var(--c-line)",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          {/* Not the error colour. This is a permission rule working correctly,
              not a failure, and colouring it red reports a healthy refusal as a
              fault — the reader's first question becomes "what broke?" */}
          <I.Alert size={16} style={{ color: "var(--c-ink-3)", flexShrink: 0 }} />
          <div id="admin-only-del-title" style={{ fontSize: 13.5, fontWeight: 600 }}>
            Riservato agli amministratori
          </div>
        </div>

        <div style={{ padding: "16px 18px" }}>
          <p style={{ fontSize: 13, lineHeight: 1.55, margin: 0 }}>{ADMIN_ONLY_DELETE_MESSAGE}</p>
          <p style={{ fontSize: 12.5, color: "var(--c-ink-3)", marginTop: 12, marginBottom: 0 }}>
            Elemento selezionato: «{subject}».
          </p>
        </div>

        <div
          style={{
            padding: "12px 18px",
            borderTop: "1px solid var(--c-line)",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <button ref={dismissRef} className="btn btn-ghost btn-sm" onClick={onDismiss}>
            Ho capito
          </button>
        </div>
      </div>
    </div>
  );
}
